import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import styled from 'styled-components';
import { useAppSelector, useAppDispatch } from '../../store/hooks';
import { updateProfile } from '../../store/slices/authSlice';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Student } from '../../types/portal';
import AvatarUploader from '../../components/common/AvatarUploader';
import Navigation from '../../components/portal/Navigation';

// Styled Components (reusing from AdminProfile with some customizations)


const PageWrapper = styled.div`
  min-height: 100vh;
  background: #ffffff;
`;

const ContentWrapper = styled.div`
  position: relative;
  z-index: 3;
  max-width: 1200px;
  margin: 0 auto;
  padding: var(--desktop-page-padding-x, 2rem);
  padding-top: 6rem;

  @media (max-width: 768px) {
    padding: 0.5rem var(--mobile-page-padding-x);
    padding-top: 1rem;
  }
`;

const ProfileCard = styled(motion.div)`
  margin-bottom: 1.5rem;
  background: #ffffff;
  color: #111827;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05), 0 1px 2px rgba(0, 0, 0, 0.06);
  transition: transform .25s ease, box-shadow .25s ease, border-color .25s ease, opacity .4s ease;
  position: relative;
  overflow: hidden;

  @media (max-width: 768px) {
    padding: 1rem;
    padding-top: 1.25rem;
    margin-bottom: 1rem;
    border-radius: 12px;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  }
`;

const ProfileHeader = styled.div.attrs({ id: 'profile-section' })`
  display: flex;
  align-items: center;
  gap: 2rem;
  margin-bottom: 2rem;
  padding: 1.5rem;
  background: #ffffff;
  border-radius: 20px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.02);

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    text-align: left;
    gap: 1.5rem;
    padding: 1.25rem;
    margin-bottom: 1.5rem;
    border-radius: 16px;
  }
`;

const MobileHeaderTop = styled.div`
  display: none;
  @media (max-width: 768px) {
    display: flex;
    align-items: center;
    gap: 1rem;
    margin-bottom: 0.5rem;
    padding-bottom: 1.25rem;
    border-bottom: 1px solid rgba(0,0,0,0.03);
    width: 100%;
    overflow: hidden;
    
    /* Ensure the avatar doesn't get squashed and has reasonable size */
    & > :first-child {
      flex-shrink: 0;
      width: 70px;
      
      /* Scale down the Avatar inside AvatarUploader for compact header */
      & > div:first-child {
        gap: 0;
        & > div:first-child {
          width: 60px !important;
          height: 60px !important;
          
          span {
            font-size: 20px !important;
          }
        }
      }

      /* Hide AvatarUploader labels/buttons in the compact mobile header */
      & > div:not(:first-child) {
        display: none;
      }
    }

    .info-container {
      flex: 1;
      min-width: 0; /* Allow container to shrink */
      display: flex;
      flex-direction: column;
      gap: 0.1rem;
    }

    h1 {
      margin: 0;
      font-size: 1.15rem;
      font-weight: 700;
      color: #111827;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      width: 100%;
    }

    .role-badge {
      margin-bottom: 0.15rem;
      font-size: 0.7rem;
      padding: 0.15rem 0.5rem;
      width: fit-content;
    }

    /* Theme support for mobile header */
    html[data-theme='facebook'] & {
      border-bottom-color: rgba(255,255,255,0.1);
      
      h1 {
        color: #ffffff;
      }
    }
  }
`;

const AvatarSection = styled.div`
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.25rem;
  padding: 1rem;
  background: rgba(248, 250, 252, 0.5);
  border-radius: 24px;

  @media (max-width: 768px) {
    display: none; // Hidden on mobile, handled by MobileHeaderTop
  }

  /* Facebook theme: adjust colors for AvatarSection */
  html[data-theme='facebook'] & {
    background: rgba(255, 255, 255, 0.1);
  }
`;

const HeaderInfo = styled.div`
  flex: 1;

  h1 {
    font-size: 2rem;
    color: #111827;
    margin-bottom: 0.5rem;
  }

  .role-badge {
    display: inline-block;
    padding: 0.5rem 1rem;
    border-radius: 20px;
    background: linear-gradient(135deg, var(--primary-orange) 0%, #ff6b35 100%);
    color: white;
    font-size: 0.875rem;
    font-weight: 600;
    margin-bottom: 0.5rem;
  }

  .header-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 1.5rem;
    margin-top: 1rem;

    @media (max-width: 768px) {
      grid-template-columns: 1fr;
      gap: 0.75rem;
      margin-top: 0.5rem;
    }
  }

  .info-item {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;

    @media (max-width: 768px) {
      flex-direction: row;
      justify-content: space-between;
      align-items: center;
      padding-bottom: 0.5rem;
      border-bottom: 1px solid rgba(0,0,0,0.015);
      
      &:last-child {
        border-bottom: none;
      }
    }
  }

  .info-label {
    font-size: 0.75rem;
    font-weight: 700;
    color: #6b7280;
    text-transform: uppercase;
    letter-spacing: 0.5px;

    @media (max-width: 768px) {
      font-size: 0.7rem;
    }
  }

  .info-value {
    color: #4b5563;
    font-size: 1rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-weight: 500;

    @media (max-width: 768px) {
      font-size: 0.85rem;
      text-align: right;
    }
  }

  .email {
    color: #4b5563;
    font-size: 1rem;
    margin-top: 0.5rem;
    display: flex;
    align-items: center;

    @media (max-width: 768px) {
      display: none; // Handled in info-grid on mobile
    }
  }

  /* Facebook theme: make headings and key texts white/mixed with Facebook colors */
  html[data-theme='facebook'] & {
    h1 {
      background: linear-gradient(135deg, #ffffff 0%, #4E69A2 100%);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
    }
    .role-badge {
      background: linear-gradient(135deg, #4E69A2 0%, #38528B 100%);
      color: #ffffff;
    }
    .email, .info-value {
      color: rgba(255, 255, 255, 0.92);
    }
    .info-label {
      color: rgba(255, 255, 255, 0.6);
    }
    .info-item {
      @media (max-width: 768px) {
        border-bottom-color: rgba(255,255,255,0.05);
      }
    }
  }

  @media (max-width: 768px) {
    h1 { display: none; }
    .role-badge { display: none; }
  }
`;

const SectionTitle = styled.h2`
  font-size: 1.5rem;
  color: #111827;
  margin-bottom: 1.5rem;

  @media (max-width: 768px) {
    font-size: 1.1rem;
    margin-bottom: 0.75rem;
    text-align: left;
    font-weight: 700;
  }

  /* Facebook theme: white→facebook gradient for section titles */
  html[data-theme='facebook'] & {
    background: linear-gradient(135deg, #ffffff 0%, #38528B 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }

  @media (max-width: 640px) {
    font-size: 1.25rem;
    margin-bottom: 1rem;
  }
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 0.75rem;
    margin-bottom: 1.25rem;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;

  label {
    font-weight: 700;
    color: #6b7280;
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 1px;
    margin-bottom: 0.25rem;
  }

  input, textarea, select {
    padding: 0.875rem;
    border: 1px solid rgba(0, 0, 0, 0.1);
    border-radius: 12px;
    font-size: 1rem;
    transition: all 0.3s ease;
    background: #f9fafb;
    color: #111827;
    width: 100%;

    @media (max-width: 640px) {
      font-size: 0.95rem;
      padding: 0.75rem;
    }

    &:focus {
      outline: none;
      border-color: var(--primary-orange);
      box-shadow: 0 0 0 3px rgba(255, 87, 34, 0.1);
    }

    &:disabled {
      background: rgba(249, 250, 251, 0.5);
      border-color: transparent;
      padding: 0.5rem 0;
      color: #111827;
      font-weight: 600;
      font-size: 1.1rem;
      cursor: default;
      -webkit-appearance: none;
      -moz-appearance: none;
      appearance: none;
    }
  }

  textarea {
    resize: vertical;
    min-height: 100px;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const Button = styled(motion.button)<{ variant?: 'primary' | 'secondary' }>`
  padding: 0.875rem 2rem;
  border-radius: 12px;
  font-weight: 600;
  font-size: 1rem;
  border: none;
  cursor: pointer;
  background: ${props => props.variant === 'secondary'
    ? 'transparent'
    : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'};
  color: ${props => props.variant === 'secondary' ? '#667eea' : 'white'};
  border: ${props => props.variant === 'secondary' ? '2px solid #667eea' : 'none'};
  box-shadow: ${props => props.variant === 'secondary'
    ? 'none'
    : '0 4px 15px rgba(102, 126, 234, 0.3)'};
  transition: all 0.3s ease;

  @media (max-width: 640px) {
    font-size: 0.95rem;
    padding: 0.75rem 1.25rem;
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${props => props.variant === 'secondary'
      ? '0 4px 15px rgba(102, 126, 234, 0.2)'
      : '0 6px 20px rgba(102, 126, 234, 0.4)'};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1.5rem;
  margin-top: 2rem;
`;

const StatCard = styled(motion.div)`
  background: #f9fafb;
  border: 1px solid rgba(0, 0, 0, 0.05);
  border-radius: 16px;
  padding: 1.5rem;
  text-align: center;

  .stat-icon {
    font-size: 2rem;
    margin-bottom: 0.5rem;
  }

  .stat-value {
    font-size: 1.5rem;
    font-weight: 700;
    color: #111827;
  }

  .stat-label {
    font-size: 0.875rem;
    color: #4b5563;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-top: 0.5rem;
  }
`;

const SuccessMessage = styled(motion.div)`
  background: linear-gradient(135deg, #11998e 0%, #38ef7d 100%);
  color: white;
  padding: 1rem;
  border-radius: 12px;
  margin-bottom: 1rem;
  text-align: center;
  font-weight: 600;
`;

const TeacherInfo = styled.div`
  background: linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%);
  border-radius: 16px;
  padding: 1.5rem;
  margin-top: 2rem;

  h3 {
    font-size: 1.25rem;
    color: #667eea;
    margin-bottom: 1rem;
  }

  .info-item {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-bottom: 0.5rem;
    color: #666;

    .icon {
      font-size: 1.25rem;
    }
  }
`;

const StudentProfile: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  useEffect(() => {
    const hash = typeof window !== 'undefined' ? window.location.hash : '';
    const shouldScroll = (location?.state as any)?.scrollTo === 'profile' || hash === '#profile';
    if (shouldScroll) {
      const el = document.getElementById('profile-section');
      if (el) {
        setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
      }
    }
  }, [location]);
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);
  const { teachers } = useAppSelector((state) => state.user);
  const [isEditing, setIsEditing] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    email: user?.email || '',
    phone: (user as Student)?.phoneNumber || (user as any)?.phone || '',
    dateOfBirth: (user as Student)?.dateOfBirth || '',
    postcode: (user as Student)?.postcode || '',
    houseNumber: (user as Student)?.houseNumber || '',
    emergencyContact: (user as Student)?.emergencyContact || '',
    licenseType: (user as Student)?.licenseType || '',
  });

    if (!user) {
        // Show a loading spinner or redirect to login
        return <div>{t('portal.common.loading', 'Loading...')}</div>;
    }
    if (user.role !== 'student') {
        // Optionally show a "Not authorized" message
        return <div>{t('portal.common.not_authorized', 'Not authorized')}</div>;
    }

  const student = user as Student;
  const assignedTeacher = student.assignedTeacher
    ? teachers.find(t => t.id === student.assignedTeacher)
    : null;

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMsg(null);
    setSaving(true);
    try {
      await dispatch(updateProfile(formData)).unwrap();
      setShowSuccess(true);
      setIsEditing(false);
      setTimeout(() => setShowSuccess(false), 3000);
    } catch (error: any) {
      const msg = typeof error === 'string' ? error : (error?.message || t('portal.profile.error', 'Failed to update profile'));
      setErrorMsg(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      firstName: user.firstName || '',
      lastName: user.lastName || '',
      email: user.email || '',
      phone: (student as any)?.phoneNumber || (user as any)?.phone || '',
      dateOfBirth: student.dateOfBirth || '',
      postcode: student.postcode || '',
      houseNumber: student.houseNumber || '',
      emergencyContact: student.emergencyContact || '',
      licenseType: student.licenseType || '',
    });
    setIsEditing(false);
  };

  const getInitials = () => {
      const firstInitial = user.firstName?.charAt(0) || user.username?.charAt(0);
      const lastInitial = user.lastName?.charAt(0) || '';
    return `${firstInitial}${lastInitial}`.toUpperCase();
  };

  return (
    <PageWrapper>
      <Navigation />
      <ContentWrapper>
        {showSuccess && (
          <SuccessMessage
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
          >
            {t('portal.profile.saved', 'Profile updated successfully!')} ✓
          </SuccessMessage>
        )}
        {errorMsg && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            style={{ background: 'linear-gradient(135deg,#fc4a1a,#f7b733)', color: 'white', padding: '1rem', borderRadius: 12, marginBottom: '1rem', textAlign: 'center', fontWeight: 600 }}
          >
            ⚠️ {errorMsg}
          </motion.div>
        )}

        <ProfileCard
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <ProfileHeader>
            <MobileHeaderTop>
              <AvatarUploader />
              <div className="info-container">
                <div className="role-badge">
                  👨‍🎓 {t('portal.register.student', 'Student')}
                </div>
                <h1>{user.firstName} {user.lastName}</h1>
              </div>
            </MobileHeaderTop>

            <AvatarSection>
              <AvatarUploader />
            </AvatarSection>
            <HeaderInfo>
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 }}
              >
                <div className="role-badge">
                  👨‍🎓 {t('portal.register.student', 'Student')}
                </div>
                <h1>{user.firstName} {user.lastName}</h1>
                <div className="header-grid">
                  <div className="info-item">
                    <div className="info-label">{t('portal.register.email', 'Email')}</div>
                    <div className="info-value">✉️ {user.email}</div>
                  </div>
                  
                  {(user.phoneNumber || user.phone) && (
                    <div className="info-item">
                      <div className="info-label">{t('portal.profile.phone', 'Phone')}</div>
                      <div className="info-value">📱 {user.phoneNumber || user.phone}</div>
                    </div>
                  )}

                  {formData.dateOfBirth && (
                    <div className="info-item">
                      <div className="info-label">{t('portal.profile.dob', 'Date of Birth')}</div>
                      <div className="info-value">🎂 {formData.dateOfBirth}</div>
                    </div>
                  )}

                  {formData.licenseType && (
                    <div className="info-item">
                      <div className="info-label">{t('portal.register.license_type', 'License Type')}</div>
                      <div className="info-value">🚗 {formData.licenseType}</div>
                    </div>
                  )}

                  {(formData.postcode || formData.houseNumber) && (
                    <div className="info-item">
                      <div className="info-label">{t('portal.profile.address', 'Address')}</div>
                      <div className="info-value">📍 {formData.postcode}{formData.houseNumber ? ` ${formData.houseNumber}` : ''}</div>
                    </div>
                  )}
                </div>
              </motion.div>
            </HeaderInfo>
          </ProfileHeader>

          <form onSubmit={handleSubmit}>
            {isEditing && (
              <>
                <SectionTitle>{t('portal.profile.personal_info', 'Personal Information')}</SectionTitle>

                <FormGrid>
                  <FormGroup>
                    <label htmlFor="firstName">{t('portal.profile.first_name', 'First Name')}</label>
                    <input
                      type="text"
                      id="firstName"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      disabled={!isEditing}
                      required
                    />
                  </FormGroup>

                  <FormGroup>
                    <label htmlFor="lastName">{t('portal.profile.last_name', 'Last Name')}</label>
                    <input
                      type="text"
                      id="lastName"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleInputChange}
                      disabled={!isEditing}
                      required
                    />
                  </FormGroup>

                  <FormGroup>
                    <label htmlFor="email">{t('portal.profile.email', 'Email Address')}</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      disabled={!isEditing}
                      required
                    />
                  </FormGroup>

                  <FormGroup>
                    <label htmlFor="phone">{t('portal.profile.phone', 'Phone Number')}</label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      disabled={!isEditing}
                      placeholder="+31 6 12345678"
                    />
                  </FormGroup>

                  <FormGroup>
                    <label htmlFor="dateOfBirth">{t('portal.profile.dob', 'Date of Birth')}</label>
                    <input
                      type="date"
                      id="dateOfBirth"
                      name="dateOfBirth"
                      value={formData.dateOfBirth}
                      onChange={handleInputChange}
                      disabled={!isEditing}
                    />
                  </FormGroup>

                  <FormGroup>
                    <label htmlFor="licenseType">{t('portal.register.license_type', 'License Type')}</label>
                    <select
                      id="licenseType"
                      name="licenseType"
                      value={formData.licenseType}
                      onChange={handleInputChange}
                      disabled={!isEditing}
                    >
                      <option value="">{t('portal.register.select_license', 'Select license type')}</option>
                      <option value="AM">{t('portal.register.license_am')}</option>
                      <option value="A1">{t('portal.register.license_a1')}</option>
                      <option value="A2">{t('portal.register.license_a2')}</option>
                      <option value="A">{t('portal.register.license_a')}</option>
                      <option value="B_MANUAL">{t('portal.register.license_b_manual')}</option>
                      <option value="B_AUTOMATIC">{t('portal.register.license_b_automatic')}</option>
                      <option value="BE">{t('portal.register.license_be')}</option>
                      <option value="B_PLUS">{t('portal.register.license_b_plus')}</option>
                      <option value="C1">{t('portal.register.license_c1')}</option>
                      <option value="C1E">{t('portal.register.license_c1e')}</option>
                      <option value="C">{t('portal.register.license_c')}</option>
                      <option value="CE">{t('portal.register.license_ce')}</option>
                      <option value="D1">{t('portal.register.license_d1')}</option>
                      <option value="D1E">{t('portal.register.license_d1e')}</option>
                      <option value="D">{t('portal.register.license_d')}</option>
                      <option value="DE">{t('portal.register.license_de')}</option>
                      <option value="T">{t('portal.register.license_t')}</option>
                    </select>
                  </FormGroup>

                  <FormGroup>
                    <label htmlFor="postcode">{t('portal.profile.address', 'Address')}</label>
                    <input
                      type="text"
                      id="postcode"
                      name="postcode"
                      value={formData.postcode}
                      onChange={handleInputChange}
                      disabled={!isEditing}
                      placeholder={String(t('portal.register.postcode_placeholder', 'Postcode'))}
                    />
                  </FormGroup>

                  <FormGroup>
                    <label htmlFor="houseNumber">{t('portal.common.house_number', 'Huisnummer')}</label>
                    <input
                      type="text"
                      id="houseNumber"
                      name="houseNumber"
                      value={formData.houseNumber}
                      onChange={handleInputChange}
                      disabled={!isEditing}
                      placeholder={String(t('portal.register.house_number_placeholder', 'Huisnummer'))}
                    />
                  </FormGroup>

                  <FormGroup>
                    <label htmlFor="emergencyContact">{t('portal.profile.emergency_contact', 'Emergency Contact')}</label>
                    <input
                      type="text"
                      id="emergencyContact"
                      name="emergencyContact"
                      value={formData.emergencyContact}
                      onChange={handleInputChange}
                      disabled={!isEditing}
                      placeholder={String(t('portal.profile.emergency_contact_placeholder', 'Name and phone number'))}
                    />
                  </FormGroup>
                </FormGrid>
              </>
            )}

            <ButtonGroup>
              {isEditing ? (
                <>
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={handleCancel}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    {t('portal.common.cancel', 'Cancel')}
                  </Button>
                  <Button
                    type="submit"
                    disabled={saving}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    {saving ? t('portal.profile.saving', 'Saving…') : t('portal.common.save_changes', 'Save Changes')}
                  </Button>
                </>
              ) : (
                <Button
                  type="button"
                  onClick={() => setIsEditing(true)}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  {t('portal.common.edit_profile', 'Edit Profile')}
                </Button>
              )}
            </ButtonGroup>
          </form>

          {assignedTeacher && (
            <TeacherInfo>
              <h3>👨‍🏫 {t('portal.profile.assigned_teacher', 'Your Assigned Teacher')}</h3>
              <div className="info-item">
                <span className="icon">👤</span>
                <span>{assignedTeacher.firstName} {assignedTeacher.lastName}</span>
              </div>
              <div className="info-item">
                <span className="icon">📧</span>
                <span>{assignedTeacher.email}</span>
              </div>
              {assignedTeacher.phone && (
                <div className="info-item">
                  <span className="icon">📱</span>
                  <span>{assignedTeacher.phone}</span>
                </div>
              )}
            </TeacherInfo>
          )}

        </ProfileCard>


      </ContentWrapper>
    </PageWrapper>
  );
};

export default StudentProfile;
